/**
 * GoodShowHQ parent education / setup guide.
 *
 * Translated from the Tailwind + lucide-react reference at
 * `design/pages/marketing/SetupGuide.jsx` into MUI 7 + @mui/icons-material.
 * The reference file is the source of truth — when this file and
 * the reference disagree, the reference wins.
 *
 * Public route: /setup. Reachable from the landing FAQ, the
 * profile-selector empty state, and the welcome flow's
 * "Read the full guide" link. No auth gate.
 *
 * Visual language matches the landing page (slate / indigo / amber
 * scale + 'GoodShowHQ' wordmark). The two pull quotes (indigo and
 * amber) are the only colored block elements on the page besides
 * the small chips — preserve their visual weight.
 */
import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import PlayCircleRounded from '@mui/icons-material/PlayCircleRounded';
import logoMark from '../../assets/brand/logo-mark.svg';

// Color tokens — same scale used on the landing page so the two
// pages share a visual language. Inlined rather than added to the
// MUI palette because they're brand-scale and not used elsewhere.
const C = {
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate900: '#0F172A',
  indigo50: '#EEF2FF',
  indigo100: '#E0E7FF',
  indigo200: '#C7D2FE',
  indigo300: '#A5B4FC',
  indigo500: '#6366F1',
  indigo600: '#3448F0',
  indigo700: '#2839C9',
  indigo800: '#1E2A9B',
  indigo900: '#172554',
  emerald100: '#D1FAE5',
  emerald500: '#10B981',
  emerald700: '#047857',
  amber50: '#FFFBEB',
  amber100: '#FEF3C7',
  amber500: '#F59E0B',
  amber600: '#D97706',
  amber700: '#B45309',
  amber800: '#92400E',
  amber900: '#78350F',
  rose100: '#FFE4E6',
  rose200: '#FECACA',
  rose600: '#E11D48',
  rose700: '#BE123C',
};

function Logo(): React.JSX.Element {
  return (
    <Box
      component="a"
      href="/"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        textDecoration: 'none',
      }}
    >
      <Box component="img" src={logoMark} alt="GoodShowHQ" sx={{width: 28, height: 28, display: 'block'}} />
      <Typography
        component="span"
        sx={{fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

function Header(): React.JSX.Element {
  return (
    <Box
      component="header"
      sx={{borderBottom: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF'}}
    >
      <Container maxWidth="md" sx={{py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Logo />
        <Box
          component="a"
          href="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: C.slate400,
            textDecoration: 'none',
            '&:hover': {color: C.slate700},
          }}
        >
          <ArrowBackRounded sx={{fontSize: 14}} />
          Back to GoodShowHQ
        </Box>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Visual aids
// ---------------------------------------------------------------------------

type MiniFeedTone = 'mixed' | 'focused-young' | 'focused-old';
interface MiniFeedProps {
  tone: MiniFeedTone;
  label: string;
  sublabel: string;
  items: string[];
}

function MiniFeed({tone, label, sublabel, items}: MiniFeedProps): React.JSX.Element {
  // Three preset tones — the "mixed" rose tone signals the
  // confused-feed problem, the two focused emerald tones signal the
  // healthy outcome.
  const styles =
    tone === 'mixed'
      ? {border: C.rose200, ringColor: C.rose100, chipBg: C.rose100, chipColor: C.rose700, chipLabel: 'Confused'}
      : tone === 'focused-young'
        ? {
            border: '#A7F3D0',
            ringColor: C.emerald100,
            chipBg: C.emerald100,
            chipColor: C.emerald700,
            chipLabel: 'Tuned to Mira, age 4',
          }
        : {
            border: '#A7F3D0',
            ringColor: C.emerald100,
            chipBg: C.emerald100,
            chipColor: C.emerald700,
            chipLabel: 'Tuned to Sam, age 9',
          };
  return (
    <Box
      sx={{
        borderRadius: 4,
        border: `1px solid ${styles.border}`,
        boxShadow: `0 0 0 1px ${styles.ringColor}`,
        bgcolor: '#FFFFFF',
        p: 2,
      }}
    >
      <Box sx={{mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Box>
          <Typography sx={{fontSize: 12, fontWeight: 700, color: C.slate900}}>{label}</Typography>
          <Typography sx={{fontSize: 10, color: C.slate500}}>{sublabel}</Typography>
        </Box>
        <Chip
          label={styles.chipLabel}
          size="small"
          sx={{
            height: 20,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            bgcolor: styles.chipBg,
            color: styles.chipColor,
            borderRadius: 999,
          }}
        />
      </Box>
      <Stack spacing={0.75}>
        {items.map(item => (
          <Box
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 1.5,
              bgcolor: C.slate50,
              px: 1,
              py: 0.75,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 24,
                flexShrink: 0,
                borderRadius: 0.75,
                bgcolor: C.slate200,
                color: C.slate400,
              }}
            >
              <PlayCircleRounded sx={{fontSize: 12}} />
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 500,
                color: C.slate700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

interface ChannelExampleProps {
  name: string;
  tag: string;
}

function ChannelExample({name, tag}: ChannelExampleProps): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        borderRadius: 3,
        border: `1px solid ${C.slate200}`,
        bgcolor: '#FFFFFF',
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${C.indigo200}, ${C.indigo300})`,
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {name[0]}
      </Box>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: C.slate900,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            color: C.slate500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tag}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function Hero(): React.JSX.Element {
  return (
    <Box sx={{textAlign: 'center'}}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          borderRadius: 999,
          bgcolor: C.indigo100,
          color: C.indigo700,
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <AutoAwesomeRounded sx={{fontSize: 14}} />
        Getting started
      </Box>
      <Typography
        component="h1"
        sx={{
          mx: 'auto',
          mt: 2,
          maxWidth: 720,
          fontSize: {xs: 36, md: 48},
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color: C.slate900,
        }}
      >
        Two things to know before you set up GoodShowHQ for your kid.
      </Typography>
      <Typography
        sx={{
          mx: 'auto',
          mt: 2,
          maxWidth: 480,
          fontSize: 16,
          lineHeight: 1.6,
          color: C.slate600,
        }}
      >
        Five-minute read. Saves you hours of fighting the algorithm later.
      </Typography>
    </Box>
  );
}

function PrincipleAnchor({n, label}: {n: number; label: string}): React.JSX.Element {
  return (
    <Box sx={{mb: 1.5, display: 'flex', alignItems: 'center', gap: 1}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: C.slate900,
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {n}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: C.slate500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function PrincipleOne(): React.JSX.Element {
  const mira = [
    'Bluey · Season 3 highlights',
    'Octonauts · deep-sea adventure',
    'Numberblocks · counting to 20',
    'How animals see the world',
  ];
  const sam = [
    'How the Eiffel Tower was built',
    'Why is the sky blue, really?',
    'Chess openings for beginners',
    'Inside a volcano, in 4K',
  ];
  const mixed = [
    'Bluey · Season 3 highlights',
    'Game Theory: every Pokémon ever',
    'Numberblocks · counting to 20',
    'MrBeast: I gave away $1M',
  ];

  return (
    <Box component="section" sx={{borderTop: `1px solid ${C.slate200}`, py: 8}}>
      <Container maxWidth="md" disableGutters>
        <PrincipleAnchor n={1} label="Principle one" />
        <Typography
          component="h2"
          sx={{
            fontSize: {xs: 28, md: 36},
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: C.slate900,
          }}
        >
          Each kid needs their own YouTube account.
        </Typography>
        <Typography sx={{mt: 2, maxWidth: 640, fontSize: 16, lineHeight: 1.7, color: C.slate700}}>
          YouTube's algorithm trains on every video that's watched on an account. If your 4-year-old and 9-year-old share an account, YouTube can't tell them apart — and serves both kids a confused middle that fits neither. The 4-year-old looking for <em>Bluey</em> gets recommended pre-teen drama; the 9-year-old who wants science explainers gets recommended toddler songs.
        </Typography>

        {/* Visual: shared vs. separate */}
        <Box sx={{mt: 4}}>
          <Typography
            sx={{
              mb: 1.5,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: C.slate500,
            }}
          >
            One shared account
          </Typography>
          <MiniFeed tone="mixed" label="Mira & Sam" sublabel="Sharing one account · ages 4 and 9" items={mixed} />
          <Typography sx={{mt: 1.5, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
            Mixed signals in, confused recommendations out. Neither kid is getting their best feed.
          </Typography>
        </Box>

        <Box sx={{mt: 5}}>
          <Typography
            sx={{
              mb: 1.5,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: C.slate500,
            }}
          >
            Two separate accounts
          </Typography>
          <Box sx={{display: 'grid', gap: 1.5, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
            <MiniFeed tone="focused-young" label="Mira" sublabel="age 4" items={mira} />
            <MiniFeed tone="focused-old" label="Sam" sublabel="age 9" items={sam} />
          </Box>
          <Typography sx={{mt: 1.5, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
            Each kid's feed is tuned to them. GoodShowHQ layers on top to filter and prioritize per-kid.
          </Typography>
        </Box>

        {/* Indigo pull quote */}
        <Box
          sx={{
            mt: 5,
            borderRadius: 4,
            borderLeft: `4px solid ${C.indigo500}`,
            bgcolor: C.indigo50,
            p: 2.5,
          }}
        >
          <Typography sx={{fontSize: 16, fontWeight: 600, lineHeight: 1.6, color: C.indigo900}}>
            "Two kids, one account = scrambled signals = a feed that fits neither."
          </Typography>
        </Box>

        {/* Practical note */}
        <Box
          sx={{
            mt: 4,
            borderRadius: 4,
            border: `1px solid ${C.slate200}`,
            bgcolor: '#FFFFFF',
            p: 2.5,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 700, color: C.slate900}}>
            How to give each kid their own account
          </Typography>
          <Typography sx={{mt: 0.75, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
            YouTube doesn't have a separate-profiles-within-one-account model (unlike Netflix). Each kid needs their own Google account, which is what they'll sign in to YouTube with.
          </Typography>
          <Stack component="ul" spacing={1} sx={{mt: 1.5, pl: 0, listStyle: 'none'}}>
            <Box component="li" sx={{display: 'flex', gap: 1}}>
              <CheckCircleRounded sx={{mt: 0.25, fontSize: 16, color: C.emerald500, flexShrink: 0}} />
              <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
                <Box component="strong" sx={{fontWeight: 700, color: C.slate900}}>For kids under 13:</Box>{' '}
                use{' '}
                <Box
                  component="a"
                  href="https://families.google.com/familylink/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{color: C.indigo700, fontWeight: 600, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                >
                  Google Family Link
                </Box>{' '}
                to create a supervised account. You stay the parent on the supervision side — you control sign-in, app permissions, and screen time. Free, takes about 5 minutes per kid.
              </Typography>
            </Box>
            <Box component="li" sx={{display: 'flex', gap: 1}}>
              <CheckCircleRounded sx={{mt: 0.25, fontSize: 16, color: C.emerald500, flexShrink: 0}} />
              <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
                <Box component="strong" sx={{fontWeight: 700, color: C.slate900}}>For kids 13 and up:</Box>{' '}
                they can have their own{' '}
                <Box
                  component="a"
                  href="https://accounts.google.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{color: C.indigo700, fontWeight: 600, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                >
                  personal Google account
                </Box>
                .
              </Typography>
            </Box>
          </Stack>
          <Typography sx={{mt: 1.5, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
            Either way, the kid signs in to YouTube with their own account. That's what gives the algorithm a clean signal about who's watching.
          </Typography>
        </Box>

        {/* Amber callout — Family Link "supervised experience" gotcha.
            Google's setup flow asks parents which YouTube tier their
            kid sees; picking anything more restrictive than the most
            permissive supervised option strips most of the
            educational catalog before GoodShowHQ gets to curate it.
            Visual treatment matches the amber pull-quote in
            PrincipleTwo so the two callouts read as one family. */}
        <Box
          sx={{
            mt: 4,
            borderRadius: 4,
            borderLeft: `4px solid ${C.amber500}`,
            bgcolor: C.amber50,
            p: 2.5,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 700, color: C.amber900}}>
            Important if you're setting up Family Link
          </Typography>
          <Typography sx={{mt: 1, fontSize: 14, lineHeight: 1.7, color: C.amber900}}>
            During Family Link setup, Google will ask what YouTube experience your kid should have. The options are roughly:{' '}
            <Box component="em" sx={{fontStyle: 'italic'}}>YouTube Kids</Box>, a more restrictive supervised tier (typically called{' '}
            <Box component="em" sx={{fontStyle: 'italic'}}>Explore</Box>), and a more permissive one (typically{' '}
            <Box component="em" sx={{fontStyle: 'italic'}}>Most of YouTube</Box>). Google often defaults to a more restrictive option based on your kid's age.
          </Typography>
          <Typography sx={{mt: 1.5, fontSize: 14, lineHeight: 1.7, color: C.amber900}}>
            <Box component="strong" sx={{fontWeight: 700}}>Choose the most permissive supervised option available</Box>{' '}
            — usually{' '}
            <Box component="em" sx={{fontStyle: 'italic'}}>"Most of YouTube"</Box>. The more restrictive tiers hide most of the educational content GoodShowHQ is designed to surface; GoodShowHQ does its own filtering on top, so you don't need Google's restrictive tier doing it twice.
          </Typography>
          <Typography sx={{mt: 1.5, fontSize: 12, lineHeight: 1.7, color: C.amber800}}>
            Heads up: even on the most permissive supervised tier, comments and live chat are disabled, and search may be limited. The video catalog itself is the same — those side restrictions don't affect GoodShowHQ's curation.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

function PrincipleTwo(): React.JSX.Element {
  // The eight named channels are the most persuasive single
  // element on the page — keep all of them.
  const adultChannels: ChannelExampleProps[] = [
    {name: 'Kurzgesagt', tag: 'Animated science explainers'},
    {name: 'Veritasium', tag: 'Physics & engineering'},
    {name: '3Blue1Brown', tag: 'Math, beautifully visualized'},
    {name: 'SciShow Kids', tag: 'Science for ages 5+'},
    {name: 'TED-Ed', tag: 'Short lessons on everything'},
    {name: 'Mark Rober', tag: 'Engineering experiments'},
    {name: 'Practical Engineering', tag: 'How infrastructure works'},
    {name: 'Tom Scott', tag: 'Curiosities of the world'},
  ];

  return (
    <Box component="section" sx={{borderTop: `1px solid ${C.slate200}`, py: 8}}>
      <Container maxWidth="md" disableGutters>
        <PrincipleAnchor n={2} label="Principle two" />
        <Typography
          component="h2"
          sx={{
            fontSize: {xs: 28, md: 36},
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: C.slate900,
          }}
        >
          Use a regular YouTube account — not YouTube Kids.
        </Typography>
        <Typography sx={{mt: 2, maxWidth: 640, fontSize: 16, lineHeight: 1.7, color: C.slate700}}>
          YouTube Kids was built around one job: preventing kids from seeing inappropriate content. That's a fine goal — but it's a different goal from <em>showing kids the things worth their time</em>. The two objectives don't overlap as much as you'd hope.
        </Typography>

        {/* Amber pull quote — the core argument */}
        <Box
          sx={{
            mt: 4,
            borderRadius: 4,
            borderLeft: `4px solid ${C.amber500}`,
            bgcolor: C.amber50,
            p: 2.5,
          }}
        >
          <Typography sx={{fontSize: 16, fontWeight: 600, lineHeight: 1.6, color: C.amber900}}>
            "YouTube Kids optimizes for what's safe to show kids. Adult YouTube has what's worth showing kids. Two different things."
          </Typography>
        </Box>

        {/* The actual problem */}
        <Box sx={{mt: 5}}>
          <Typography sx={{fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}>
            The best educational creators don't make "kids content."
          </Typography>
          <Typography sx={{mt: 1.5, fontSize: 16, lineHeight: 1.7, color: C.slate700}}>
            Most of YouTube's substantive educational content was made for a general audience. Their target is curious humans aged 10 to 100, not "kids" specifically. YouTube Kids walls those creators off entirely — they're not in its catalog at all.
          </Typography>
          <Typography sx={{mt: 1.5, fontSize: 16, lineHeight: 1.7, color: C.slate700}}>
            By age 5 or 6, your kid is ready to learn from creators like these. By 8 or 9, they're capable of following physics and engineering explainers that adults find rewarding. YouTube Kids never gets there.
          </Typography>

          <Typography
            sx={{
              mt: 3,
              mb: 1.5,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: C.slate500,
            }}
          >
            Examples your kid won't find on YouTube Kids
          </Typography>
          <Box sx={{display: 'grid', gap: 1, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
            {adultChannels.map(c => (
              <ChannelExample key={c.name} name={c.name} tag={c.tag} />
            ))}
          </Box>
          <Typography sx={{mt: 1.5, fontSize: 12, color: C.slate500}}>
            And many more. Most of YouTube's high-substance educational channels are general-audience only.
          </Typography>
        </Box>

        {/* What YouTube Kids actually surfaces */}
        <Box sx={{mt: 6}}>
          <Typography sx={{fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}>
            And the algorithm wasn't built for quality.
          </Typography>
          <Typography sx={{mt: 1.5, fontSize: 16, lineHeight: 1.7, color: C.slate700}}>
            YouTube Kids' recommendation engine is tuned for engagement and safety, not substance. The result is a steady churn of mass-produced "kid-shaped content" — slime videos, nursery-rhyme remixes, unboxings, and clips made specifically to feed the algorithm's appetite. Some of it is fine. Most of it is empty calories. Almost none of it teaches your kid anything.
          </Typography>
        </Box>

        {/* Counterargument */}
        <Box
          sx={{
            mt: 5,
            borderRadius: 4,
            border: `1px solid ${C.slate200}`,
            bgcolor: C.slate50,
            p: 2.5,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 700, color: C.slate900}}>
            "But isn't adult YouTube full of stuff my kid shouldn't see?"
          </Typography>
          <Typography sx={{mt: 0.75, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
            Yes — and that's exactly what GoodShowHQ is for. We let your kid into adult YouTube and use AI to remove what they shouldn't see, so what's left is the genuinely educational, age-appropriate stuff. You get the upside of the full library without the downside of a free-for-all.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

function WhereGoodShowHQFits(): React.JSX.Element {
  return (
    <Box component="section" sx={{borderTop: `1px solid ${C.slate200}`, bgcolor: C.slate50, py: 8}}>
      <Container maxWidth="md" disableGutters>
        <Box sx={{textAlign: 'center'}}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              borderRadius: 999,
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
              color: C.slate700,
              px: 1.5,
              py: 0.5,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <AutoAwesomeRounded sx={{fontSize: 14, color: C.indigo600}} />
            How GoodShowHQ fits in
          </Box>
          <Typography
            component="h2"
            sx={{
              mx: 'auto',
              mt: 2,
              maxWidth: 640,
              fontSize: {xs: 28, md: 36},
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: C.slate900,
            }}
          >
            Think of it as a librarian, not a wall.
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 5,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {xs: '1fr', md: '1fr 1fr 1fr'},
          }}
        >
          {/* Ball pit — rose */}
          <Box sx={{borderRadius: 4, border: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', p: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: C.rose100,
                color: C.rose600,
              }}
            >
              <CloseRounded sx={{fontSize: 22, fontWeight: 800}} />
            </Box>
            <Typography sx={{mt: 2, fontSize: 16, fontWeight: 800, color: C.slate900}}>
              YouTube Kids = the ball pit
            </Typography>
            <Typography sx={{mt: 1, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
              Safe, simple, low ceiling. Fine for very young kids. Empty calories for older ones.
            </Typography>
          </Box>

          {/* Library — amber */}
          <Box sx={{borderRadius: 4, border: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', p: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: C.amber100,
                color: C.amber600,
              }}
            >
              <MenuBookRounded sx={{fontSize: 22}} />
            </Box>
            <Typography sx={{mt: 2, fontSize: 16, fontWeight: 800, color: C.slate900}}>
              Adult YouTube = the library
            </Typography>
            <Typography sx={{mt: 1, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
              Has everything worth knowing — including the genuinely educational stuff. Also has things kids shouldn't see.
            </Typography>
          </Box>

          {/* Librarian — indigo (highlighted) */}
          <Box sx={{borderRadius: 4, border: `1px solid ${C.indigo300}`, bgcolor: C.indigo50, p: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: C.indigo600,
                color: '#FFFFFF',
              }}
            >
              <ShieldRounded sx={{fontSize: 22}} />
            </Box>
            <Typography sx={{mt: 2, fontSize: 16, fontWeight: 800, color: C.slate900}}>
              GoodShowHQ = the librarian
            </Typography>
            <Typography sx={{mt: 1, fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
              Lets your kid into the library. Quietly removes what they shouldn't read. Recommends what's actually good for them.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function SetupSteps(): React.JSX.Element {
  const navigate = useNavigate();
  const goToAuth = () => navigate('/auth');
  const steps = [
    {
      title: 'Create a Google account for your kid',
      body: 'Under 13: use Google Family Link to set up a supervised account. 13 and up: a personal Google account. The kid signs in to YouTube with this account on their own device — that\'s the account whose feed GoodShowHQ will curate.',
    },
    {
      title: 'Install GoodShowHQ Desktop on their computer',
      body: 'The Desktop app links your kid\'s YouTube account to GoodShowHQ — it signs them in once on a real browser so we can pair their account with our review pipeline. After that\'s done you can manage everything from the GoodShowHQ mobile app; the Desktop app stays out of the way in the background.',
    },
    {
      title: 'Open the parent dashboard',
      body: 'Within a few minutes you\'ll see your kid\'s profile show up in the dashboard. From there, GoodShowHQ starts learning what\'s right for them.',
    },
  ];

  return (
    <Box component="section" sx={{borderTop: `1px solid ${C.slate200}`, py: 8}}>
      <Container maxWidth="md" disableGutters>
        <Typography
          component="h2"
          sx={{
            fontSize: {xs: 28, md: 32},
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: C.slate900,
          }}
        >
          Set it up
        </Typography>
        <Typography sx={{mt: 1, fontSize: 16, color: C.slate600}}>
          About five minutes per kid.
        </Typography>
        <Stack component="ol" spacing={2.5} sx={{mt: 4, pl: 0, listStyle: 'none'}}>
          {steps.map((s, i) => (
            <Box component="li" key={s.title} sx={{display: 'flex', gap: 2}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: C.slate900,
                  color: '#FFFFFF',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {i + 1}
              </Box>
              <Box>
                <Typography sx={{fontSize: 16, fontWeight: 700, color: C.slate900}}>
                  {s.title}
                </Typography>
                <Typography sx={{mt: 0.5, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
                  {s.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 5,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${C.indigo600}, ${C.indigo800})`,
            color: '#FFFFFF',
            p: 3,
          }}
        >
          <Typography sx={{fontSize: 18, fontWeight: 800}}>Ready to set up?</Typography>
          <Typography sx={{mt: 0.5, fontSize: 14, lineHeight: 1.7, color: C.indigo100}}>
            Sign in to your GoodShowHQ account and we'll walk you through the steps for each kid.
          </Typography>
          <Button
            onClick={goToAuth}
            endIcon={<ArrowForwardRounded sx={{fontSize: 16}} />}
            sx={{
              mt: 2,
              borderRadius: 999,
              bgcolor: '#FFFFFF',
              color: C.indigo700,
              px: 2.5,
              py: 1.25,
              fontSize: 14,
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {bgcolor: C.indigo50},
            }}
          >
            Get started
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

function FAQ(): React.JSX.Element {
  const items = [
    {
      q: 'What if my kid is 3 and YouTube Kids is genuinely fine for them?',
      a: 'YouTube Kids is a reasonable choice for toddlers — short attention spans, very limited horizon, mostly nursery-rhyme content. By age 4 or 5, most kids are ready for more substantive content, and that\'s where the upgrade path starts. GoodShowHQ isn\'t aimed at the under-3 crowd.',
    },
    {
      q: 'Won\'t my kid see something inappropriate before GoodShowHQ filters it?',
      a: 'GoodShowHQ doesn\'t intercept YouTube — your kid still uses regular YouTube as-is. What we do is two things: (a) we monitor what shows up in their feed and rate it for you, so you can see what the algorithm is steering them toward; and (b) we monitor what they actually watch from their history and notify you when something looks off. Over time the picture sharpens — what they see, what they choose, and where you may want to step in.',
    },
    {
      q: 'Do I need a Google account for each kid?',
      a: 'Yes — YouTube uses the Google account to identify whose feed it is, and there\'s no Netflix-style multi-profile-within-one-account model. For kids under 13, set up a supervised account via Google Family Link (you stay the parent on the supervision side, controlling sign-in and screen-time settings). For kids 13 and up, they can have their own personal Google account. Either takes about 5 minutes and is free.',
    },
    {
      q: 'What about kids\' content creators that are great — Blippi, Ms. Rachel, etc.?',
      a: 'Many of those creators are on regular YouTube too, with full channels. GoodShowHQ will surface the high-quality kids\' creators alongside the general-audience educational ones. You don\'t have to give up the good kid-specific stuff to get the upside of adult YouTube.',
    },
  ];

  return (
    <Box component="section" sx={{borderTop: `1px solid ${C.slate200}`, py: 8}}>
      <Container maxWidth="md" disableGutters>
        <Typography
          component="h2"
          sx={{fontSize: {xs: 28, md: 32}, fontWeight: 800, letterSpacing: '-0.02em', color: C.slate900}}
        >
          Common questions
        </Typography>
        <Stack spacing={1.5} sx={{mt: 4}}>
          {items.map(item => (
            <Box
              key={item.q}
              sx={{borderRadius: 4, border: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', p: 2.5}}
            >
              <Typography sx={{fontSize: 16, fontWeight: 700, color: C.slate900}}>{item.q}</Typography>
              <Typography sx={{mt: 1, fontSize: 14, lineHeight: 1.7, color: C.slate600}}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

function Footer(): React.JSX.Element {
  return (
    <Box component="footer" sx={{borderTop: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', py: 4}}>
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', md: 'row'},
          alignItems: {xs: 'flex-start', md: 'center'},
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Logo />
        <Typography sx={{fontSize: 12, color: C.slate500}}>
          Not affiliated with YouTube or Google LLC. © 2026 GoodShowHQ, Inc.
        </Typography>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SetupGuide(): React.JSX.Element {
  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#FFFFFF', color: C.slate900}}>
      <Header />
      <Box component="main">
        <Box
          sx={{
            borderBottom: `1px solid ${C.slate200}`,
            background: `linear-gradient(180deg, #FFFFFF, ${C.slate50})`,
            px: 3,
            py: {xs: 8, md: 10},
          }}
        >
          <Container maxWidth="md" disableGutters>
            <Hero />
          </Container>
        </Box>
        <Box sx={{px: 3}}>
          <PrincipleOne />
        </Box>
        <Box sx={{px: 3}}>
          <PrincipleTwo />
        </Box>
        <Box sx={{px: 3}}>
          <WhereGoodShowHQFits />
        </Box>
        <Box sx={{px: 3}}>
          <SetupSteps />
        </Box>
        <Box sx={{px: 3}}>
          <FAQ />
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
